import { MILLIS_PER_HOUR } from '@/src/constants/DateTimeConstants';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { FlashbarProps } from '@amzn/awsui-components-react';
import { mqtt5 } from 'aws-iot-device-sdk-v2/dist/browser';
import { unescape } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EnableMessagingResponse } from '../types/EnableMessagingResponse';
import { NotificationData } from '../types/JamNotifications';
import { WebSocketsMessage, WsMessageType, AsyncResultMessageBody } from '../types/WebsocketMessages';
import { localLogger } from '../utils/log.utils';
import { MQTTService } from '../utils/mqtt.utils';
import { useApi } from '../store/api.context';
import { useFlashbars } from '../store/flashbar.context';
import { useUser } from '../store/user.context';
import { config } from '../config/app-config';

/**
 * Fetch JSON content from a presigned S3 URL.
 */
export const fetchAsyncResult = async <T = any>(s3Url: string): Promise<T> => {
  const response = await fetch(s3Url);
  if (!response.ok) {
    throw new Error(`Failed to fetch from S3: ${response.status} ${response.statusText}`);
  }
  return (await response.json()) as T;
};

export interface UserWebsocketValue {
  isLoaded: boolean;
  destroyConnection: () => void;
  onAsyncResult: (jobId: string, callback: (data: any, error?: Error) => void) => void;
  offAsyncResult: (jobId: string) => void;
  fetchAsyncResultFromS3: (s3Url: string) => Promise<any>;
}

const MAX_CONNECTION_FAILURE_RETRIES = 5;

const safeDecode = (value: string): string => {
  try {
    return unescape(decodeURIComponent(value));
  } catch {
    return value;
  }
};

const useUserWebsocket = (): UserWebsocketValue => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { jamMessageApi } = useApi();
  const { t } = useTranslation();
  const mqttClient = useRef<MQTTService>();
  const credentials = useRef<EnableMessagingResponse | undefined>();
  const credentialsRetrieved = useRef<number>(0);
  const connectionErrorsCount = useRef<number>(0);
  const connectionErrorFlashbarId = useRef<string>('');
  const initializingRef = useRef(false);
  const { user } = useUser();
  const { addFlashbar } = useFlashbars();
  const userExist = !!user?.email;

  const addFlashbarRef = useRef(addFlashbar);
  const tRef = useRef(t);

  useEffect(() => {
    addFlashbarRef.current = addFlashbar;
  }, [addFlashbar]);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const asyncResultCallbacks = useRef<Map<string, (data: any, error?: Error) => void>>(new Map());

  const getCredentials = useCallback(async () => {
    if (
      !credentials.current ||
      !(credentialsRetrieved.current && credentialsRetrieved.current + MILLIS_PER_HOUR > Date.now())
    ) {
      const response = await jamMessageApi.enableUserMessaging({ silent: true });
      credentials.current = response;
      credentialsRetrieved.current = Date.now();
      return response;
    }
    return credentials.current;
  }, [jamMessageApi]);

  const onConnectionSuccess = () => {
    connectionErrorsCount.current = 0;
    connectionErrorFlashbarId.current = '';
  };

  const onAsyncResultMessage = async (messageBody: AsyncResultMessageBody) => {
    const { jobId, s3Url } = messageBody;
    try {
      const data = await fetchAsyncResult(s3Url);
      const callback = asyncResultCallbacks.current.get(jobId);
      if (callback) {
        callback(data);
        asyncResultCallbacks.current.delete(jobId);
      } else {
        localLogger('No callback registered for async result jobId:', jobId);
      }
    } catch (error) {
      const callback = asyncResultCallbacks.current.get(jobId);
      if (callback) {
        callback(null, error instanceof Error ? error : new Error(String(error)));
        asyncResultCallbacks.current.delete(jobId);
      }
    }
  };

  const onReceiveMessage: mqtt5.MessageReceivedEventListener = (receivedEvent) => {
    const message = JSON.parse(receivedEvent.message.payload as string) as WebSocketsMessage<any>;
    localLogger('received user WS message', receivedEvent.message.topicName, message);
    switch (message.messageType) {
      case WsMessageType.NotificationWSMessage:
        onNotificationMessage(message.messageBody);
        break;
      case WsMessageType.ChallengeRecommendationWSMessage:
        void onAsyncResultMessage(message.messageBody);
        break;
      default:
        localLogger('Unhandled user websocket message type:', message.messageType);
    }
  };

  const onNotificationMessage = (notificationData: NotificationData) => {
    const {
      notificationMessage: { message, title, type },
      options,
    } = notificationData;
    if (!(['success', 'warning', 'info', 'error'] as FlashbarProps.Type[]).includes(type as FlashbarProps.Type)) {
      return;
    }
    addFlashbarRef.current({
      content: safeDecode(message),
      header: safeDecode(title),
      type: type as FlashbarProps.Type,
      dismissible: true,
      i18nKeys: [],
      id: `${new Date().getTime()}`,
      autoDismiss: !!options.timeOut,
      dismissDuration: options.timeOut,
    });
  };

  const onCredentialsUpdated = async (): Promise<EnableMessagingResponse | undefined> => {
    if (mqttClient.current) {
      return await getCredentials();
    }
  };

  const onConnectionFailure = async (): Promise<EnableMessagingResponse | undefined> => {
    connectionErrorsCount.current = connectionErrorsCount.current + 1;
    if (connectionErrorsCount.current > MAX_CONNECTION_FAILURE_RETRIES) {
      if (!connectionErrorFlashbarId.current) {
        const id = `${new Date().getTime()}`;
        addFlashbarRef.current({
          content: tRef.current(i18nKeys.errors.pleaseRefresh),
          header: tRef.current(i18nKeys.errors.network.connectionAborted),
          type: 'warning',
          dismissible: true,
          i18nKeys: [],
          onDismiss: () => {
            connectionErrorFlashbarId.current = '';
          },
          id,
        });
        connectionErrorFlashbarId.current = id;
      }
      return;
    }
    if (mqttClient.current) {
      return await getCredentials();
    }
  };

  const initialize = async () => {
    if (initializingRef.current) return;
    initializingRef.current = true;
    try {
      const creds = await getCredentials();
      if (creds) {
        mqttClient.current = new MQTTService(creds);
        mqttClient.current.listen({ onReceiveMessage, onCredentialsUpdated, onConnectionFailure, onConnectionSuccess });
      }
    } catch (e) {
      localLogger('error initializing user mqtt client', e);
    } finally {
      initializingRef.current = false;
      setIsLoaded(true);
    }
  };

  const destroyConnection = () => {
    localLogger('user connection destroying');
    void mqttClient.current?.destroy();
  };

  const onAsyncResult = (jobId: string, callback: (data: any, error?: Error) => void) => {
    asyncResultCallbacks.current.set(jobId, callback);
  };

  const offAsyncResult = (jobId: string) => {
    asyncResultCallbacks.current.delete(jobId);
  };

  const fetchAsyncResultFromS3 = async (s3Url: string): Promise<any> => {
    return fetchAsyncResult(s3Url);
  };

  const { enableAsyncChallengeRecommendations } = config.ff;

  useEffect(() => {
    if (userExist && enableAsyncChallengeRecommendations) {
      void initialize();
    }
    return () => destroyConnection();
  }, [userExist, enableAsyncChallengeRecommendations]);

  return {
    isLoaded,
    destroyConnection,
    onAsyncResult,
    offAsyncResult,
    fetchAsyncResultFromS3,
  };
};

export { useUserWebsocket };
